package me.tbsten.compose.preview.lab.previewlab.header

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import me.tbsten.compose.preview.lab.ui.PreviewLabTheme
import me.tbsten.compose.preview.lab.ui.components.PreviewLabIconButton
import me.tbsten.compose.preview.lab.ui.components.PreviewLabText
import me.tbsten.compose.preview.lab.ui.generated.resources.PreviewLabUiRes
import me.tbsten.compose.preview.lab.ui.generated.resources.icon_refresh
import me.tbsten.compose.preview.lab.ui.generated.resources.icon_zoom_in
import me.tbsten.compose.preview.lab.ui.generated.resources.icon_zoom_out
import org.jetbrains.compose.resources.painterResource

@Composable
internal fun Zoom(scale: Float, onScaleChange: (Float) -> Unit, onOffsetReset: () -> Unit, modifier: Modifier = Modifier) {
    Column(modifier = modifier) {
        PreviewLabText("Zoom", style = PreviewLabTheme.typography.label2)

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f, fill = false)
                .padding(vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            PreviewLabIconButton(
                painter = painterResource(PreviewLabUiRes.drawable.icon_zoom_in),
                contentDescription = "Zoom In",
                enabled = scale < MaxZoomScale,
                onClick = {
                    onScaleChange(scale.nextZoomInScale())
                },
            )

            PreviewLabIconButton(
                painter = painterResource(PreviewLabUiRes.drawable.icon_zoom_out),
                contentDescription = "Zoom Out",
                enabled = MinZoomScale < scale,
                onClick = {
                    onScaleChange(scale.nextZoomOutScale())
                },
                modifier = Modifier.weight(1f),
            )

            PreviewLabIconButton(
                painter = painterResource(PreviewLabUiRes.drawable.icon_refresh),
                contentDescription = "Zoom Reset",
                onClick = {
                    onScaleChange(1.00f)
                    onOffsetReset()
                },
            )
        }
    }
}

internal const val MinZoomScale = 0.10f
internal const val MaxZoomScale = 10.00f

/**
 * Calculates the next zoom-in scale from the current zoom ratio.
 *
 * Below 1.0x increases by 0.1x, 1.0-2.0x by 0.25x, 2.0x or above by 1.0x.
 * Inputs outside the valid range (NaN, +/-Infinity, negatives, or `> MaxZoomScale`)
 * are sanitized first, and the result is always clamped into `[MinZoomScale, MaxZoomScale]`.
 *
 * Non-finite or negative scales must never leak out: a negative `contentScale` would make the
 * grid drawing loop in `ContentSection` (`while (gridX <= size.width) { gridX += gridSize }`)
 * loop forever because `gridSize` becomes non-positive.
 */
internal fun Float.nextZoomInScale(): Float {
    val sanitized = this.sanitizeForZoom()
    val next = when (sanitized) {
        in MinZoomScale..<1.0f -> sanitized + 0.10f
        in 1.0f..<2.0f -> sanitized + 0.25f
        else -> sanitized + 1.00f
    }
    return next.coerceIn(MinZoomScale, MaxZoomScale)
}

/**
 * Calculates the next zoom-out scale from the current zoom ratio.
 *
 * Below 1.0x decreases by 0.1x, 1.0-2.0x by 0.25x, 2.0x or above by 1.0x.
 * Inputs outside the valid range (NaN, +/-Infinity, negatives, or `> MaxZoomScale`)
 * are sanitized first, and the result is always clamped into `[MinZoomScale, MaxZoomScale]`.
 *
 * Non-finite or negative scales must never leak out: a negative `contentScale` would make the
 * grid drawing loop in `ContentSection` (`while (gridX <= size.width) { gridX += gridSize }`)
 * loop forever because `gridSize` becomes non-positive.
 */
internal fun Float.nextZoomOutScale(): Float {
    val sanitized = this.sanitizeForZoom()
    val next = when (sanitized) {
        in MinZoomScale..<1.0f -> sanitized - 0.10f
        in 1.0f..<2.0f -> sanitized - 0.25f
        else -> sanitized - 1.00f
    }
    return next.coerceIn(MinZoomScale, MaxZoomScale)
}

/**
 * Sanitizes a raw zoom scale input by replacing non-finite values (NaN, +/-Infinity) and
 * values outside `[MinZoomScale, MaxZoomScale]` with the nearest valid bound.
 *
 * This guarantees the result is finite and in `[MinZoomScale, MaxZoomScale]` so that downstream
 * arithmetic (`+ 0.1f`, `* contentScale`, etc.) cannot produce negative or non-finite scales.
 */
private fun Float.sanitizeForZoom(): Float = if (isFinite()) coerceIn(MinZoomScale, MaxZoomScale) else MinZoomScale
